import * as React from 'react';

import styled from '@emotion/styled';
import capitalize from 'lodash-es/capitalize';

import Label, { type LabelProps } from '~/components/Label/Label';
import { type Option } from '~/components/Select';
import { colors } from '~/styles/constants';

interface CommonProps {
  dataAnalytics?: string;
  disabled?: boolean;
  htmlFor: string;
  vertical?: boolean;
}

const OptionsContainer = styled('div')`
  display: flex;

  & > div {
    margin-right: 13px;
  }

  & > div:last-child {
    margin-right: 0;
  }
`;

const RadioInput = styled('input')`
  margin: 0;
  margin-right: 5px;
  position: relative;
  &:checked::after {
    content: '';
    display: block;
    position: relative;
    background: ${colors.forest700};
    width: 7px;
    height: 7px;
    border: 0;
    border-radius: 50%;
    top: 3px;
    left: 3px;
  }
  &:checked::before {
    content: '';
    display: block;
    position: absolute;
    background: white;
    border: 1px solid ${colors.forest700};
    width: 11px;
    height: 11px;
    border-radius: 50%;
    top: 0px;
    left: 0px;
  }
`;

const RadioLabel = styled('label')<CommonProps>`
  color: ${(props) => (props.disabled ? colors.form.disabledLabelFontColor : colors.black)};
  padding: ${(props) => (props.vertical ? '8px 0' : '0')};
  cursor: pointer;
`;

const RadioContainer = styled('div')`
  display: flex;
  align-items: center;
`;

export interface OwnFieldProps extends CommonProps {
  children?: React.ReactNode;
  className?: string;
  errorMessage?: string;
  hideErrors?: boolean;
  invalid?: boolean;
  label?: string;
  labelProps?: LabelProps;
  options: Option[];
  tabIndex?: number;
}

interface RadioTooltipsProps {
  toolTipContent?: React.ReactNode;
}
interface OwnProps extends OwnFieldProps {
  name: string;
  onChange(option: Option, evt: React.ChangeEvent<HTMLInputElement>): void;
  value: Maybe<Option>;
}

const Radio = (props: OwnProps & RadioTooltipsProps) => {
  const { dataAnalytics, disabled, label, name, onChange, options, toolTipContent, value } = props;

  const onChangeCallback = React.useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    const option = options.find((option) => option.value === evt.target.value);

    if (option) return onChange(option, evt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {label && <Label {...props.labelProps}>{label}</Label>}
      <OptionsContainer
        className={props.className}
        style={
          props.vertical
            ? { alignItems: 'flex-start', flexDirection: 'column' }
            : { alignItems: 'center', flexDirection: 'row' }
        }
      >
        {options?.map((option, idx) => (
          <RadioContainer key={idx}>
            <RadioInput
              checked={option.value === value?.value}
              data-analytics={
                dataAnalytics && `${dataAnalytics}${capitalize(option.value?.toString())}Input`
              }
              disabled={disabled}
              id={option.label as string}
              name={name}
              onChange={onChangeCallback}
              type="radio"
              value={String(option.value)}
            />
            <RadioLabel
              data-analytics={
                dataAnalytics && `${dataAnalytics}${capitalize(option.value?.toString())}Label`
              }
              disabled={disabled}
              htmlFor={option.label as string}
              vertical={props.vertical}
            >
              {option.label}
            </RadioLabel>
            {option.addToolTip && toolTipContent}
          </RadioContainer>
        ))}
      </OptionsContainer>
    </div>
  );
};

export default Radio;
